import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../firebaseConfig';
import styles from './PlayerProfileForm.module.css';
import { FaEdit } from 'react-icons/fa';

const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const PlayerProfileForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    location: '',
    nationality: '',
    sports: '',
    profileImage: null,
    height: '',
    weight: '',
    dominantHand: '',
    position: '',
    jerseyNumber: '',
    careerLevel: '',
    yearsExperience: '',
    titles: '',
    currentRanking: '',
    gamesPlayed: '',
    averageScore: '',
    assistsPerGame: '',
    winRate: '',
    status: '',
    preferredPlayTime: '',
    email: '',
    provider: '',
    uid: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isProfileExisting, setIsProfileExisting] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [imageCacheBuster, setImageCacheBuster] = useState(Date.now());

  useEffect(() => {
    const fetchProfile = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return;
      }
      
      const { uid, email, providerData } = currentUser;
      const provider = providerData[0]?.providerId || 'Unknown';
      
      // Fetch user by email to get MongoDB _id
      try {
        const userRes = await fetch(`${BACKEND_URL}/api/users?email=${encodeURIComponent(email)}`);
        
        if (userRes.ok) {
          const userData = await userRes.json();
          setUserId(userData._id);
        } else {
          console.error('Failed to fetch user from backend:', userRes.status, userRes.statusText);
          const errorText = await userRes.text();
          console.error('Error response:', errorText);
        }
      } catch (err) {
        console.error('Error fetching user from backend:', err);
        // Try to create user if fetch fails
        try {
          const createRes = await fetch(`${BACKEND_URL}/api/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: email,
              name: currentUser.displayName || '',
              profilePicture: currentUser.photoURL || ''
            })
          });
          
          if (createRes.ok) {
            const newUserData = await createRes.json();
            setUserId(newUserData.userId || newUserData.user._id);
          } else {
            console.error('Failed to create user in backend:', createRes.status);
          }
        } catch (createErr) {
          console.error('Error creating user in backend:', createErr);
        }
      }
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/playerProfiles?uid=${uid}`);
        if (response.data) {
          const profile = response.data;
          setFormData({ ...profile, email, provider, uid });
          setProfileId(profile._id);
          setIsProfileExisting(true);
          setIsEditing(false);
        } else {
          setFormData((prev) => ({ ...prev, email, provider, uid }));
        }
      } catch (error) {
        console.error('Error fetching profile:', error.message);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        alert(`Image size is too large. Please select a file smaller than ${MAX_IMAGE_SIZE_MB} MB.`);
        e.target.value = '';
        return;
      } else {
        setFormData((prev) => ({ ...prev, profileImage: file }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      alert('User must be authenticated. Please log in again.');
      return;
    }
    try {
      const dataToSend = new FormData();
      // Required fields
      dataToSend.append('userId', userId);
      if (formData.uid) dataToSend.append('uid', formData.uid);
      dataToSend.append('name', formData.name);
      dataToSend.append('age', formData.age);
      dataToSend.append('location', formData.location);
      dataToSend.append('nationality', formData.nationality);
      // Sports as JSON string
      if (Array.isArray(formData.sports)) {
        dataToSend.append('sports', JSON.stringify(formData.sports));
      } else {
        dataToSend.append('sports', formData.sports);
      }
      dataToSend.append('height', formData.height);
      dataToSend.append('weight', formData.weight);
      dataToSend.append('dominantHand', formData.dominantHand);
      dataToSend.append('position', formData.position);
      dataToSend.append('jerseyNumber', formData.jerseyNumber);
      dataToSend.append('careerLevel', formData.careerLevel);
      dataToSend.append('yearsExperience', formData.yearsExperience);
      dataToSend.append('gamesPlayed', formData.gamesPlayed);
      dataToSend.append('averageScore', formData.averageScore);
      dataToSend.append('assistsPerGame', formData.assistsPerGame);
      dataToSend.append('winRate', formData.winRate);
      dataToSend.append('status', formData.status);
      dataToSend.append('preferredPlayTime', formData.preferredPlayTime);
      // Optional image
      if (formData.profileImage && typeof formData.profileImage !== 'string') {
        dataToSend.append('profileImage', formData.profileImage);
      }
      
      let response;
      if (isProfileExisting) {
        response = await axios.put(`${BACKEND_URL}/api/playerProfiles/${profileId}`, dataToSend);
      } else {
        response = await axios.post(`${BACKEND_URL}/api/playerProfiles`, dataToSend);
      }
      setFormData(response.data);
      setProfileId(response.data._id);
      setIsProfileExisting(true);
      alert(isProfileExisting ? 'Profile updated successfully' : 'Profile created successfully');
      setImageCacheBuster(Date.now());
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error.message);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  // Helper for displaying sports as a string
  const sportsString = Array.isArray(formData.sports)
    ? formData.sports.join(', ')
    : formData.sports;

  return (
    <div className={styles.personaContainer}>
      <form className={styles.personaCard} onSubmit={handleSubmit}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          <div className={styles.profileImageWrapper}>
            <img
              src={profileId ? `${BACKEND_URL}/api/playerProfiles/image/${profileId}?cb=${imageCacheBuster}` : 'placeholder.jpg'}
              alt="Profile"
              className={styles.profileImage}
            />
            {!isEditing && (
              <button
                type="button"
                className={styles.editIconButton}
                onClick={handleEdit}
                aria-label="Edit Profile Picture"
              >
                <FaEdit />
              </button>
            )}
          </div>
          {isEditing && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Profile Image</label>
              <input type="file" accept="image/*" onChange={handleImageChange} className={styles.formInput} />
            </div>
          )}
          <div className={styles.nameSection}>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={styles.personaNameInput}
                placeholder="Full Name"
                required
              />
            ) : (
              <h1 className={styles.personaName}>{formData.name || 'Player Name'}</h1>
            )}
            <div className={styles.personaSubtitle}>USER PERSONA</div>
          </div>
          <div className={styles.leftSummaryCard}>
            <div><b>Age:</b> {isEditing ? (
              <input type="number" name="age" value={formData.age} onChange={handleChange} className={styles.formInput} required />
            ) : (
              formData.age || 'N/A'
            )}</div>
            <div><b>Location:</b> {isEditing ? (
              <input type="text" name="location" value={formData.location} onChange={handleChange} className={styles.formInput} required />
            ) : (
              formData.location || 'N/A'
            )}</div>
            <div><b>Nationality:</b> {isEditing ? (
              <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} className={styles.formInput} required />
            ) : (
              formData.nationality || 'N/A'
            )}</div>
            <div><b>Sports:</b> {isEditing ? (
              <input type="text" name="sports" value={sportsString} onChange={handleChange} className={styles.formInput} required />
            ) : (
              sportsString || 'N/A'
            )}</div>
            <div><b>Career Level:</b> {isEditing ? (
              <select name="careerLevel" value={formData.careerLevel} onChange={handleChange} className={styles.formInput} required>
                <option value="">Select...</option>
                <option value="Amateur">Amateur</option>
                <option value="Professional">Professional</option>
              </select>
            ) : (
              formData.careerLevel || 'N/A'
            )}</div>
          </div>
        </div>
        {/* Right Column */}
        <div className={styles.rightColumn}>
          {/* Physical Stats */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>Physical Stats</div>
            <div className={styles.cardContent}>
              <div className={styles.formGroup}><label className={styles.formLabel}>Height</label>
                {isEditing ? (
                  <input type="text" name="height" value={formData.height} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.height || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Weight</label>
                {isEditing ? (
                  <input type="text" name="weight" value={formData.weight} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.weight || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Dominant Hand</label>
                {isEditing ? (
                  <select name="dominantHand" value={formData.dominantHand} onChange={handleChange} className={styles.formInput} required>
                    <option value="">Select...</option>
                    <option value="Right">Right</option>
                    <option value="Left">Left</option>
                    <option value="Ambidextrous">Ambidextrous</option>
                  </select>
                ) : (
                  <span>{formData.dominantHand || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Position</label>
                {isEditing ? (
                  <input type="text" name="position" value={formData.position} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.position || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Jersey Number</label>
                {isEditing ? (
                  <input type="number" name="jerseyNumber" value={formData.jerseyNumber} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.jerseyNumber || 'N/A'}</span>
                )}
              </div>
            </div>
          </div>
          {/* Achievements */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>Achievements</div>
            <div className={styles.cardContent}>
              <div className={styles.formGroup}><label className={styles.formLabel}>Career Level</label>
                {isEditing ? (
                  <select name="careerLevel" value={formData.careerLevel} onChange={handleChange} className={styles.formInput} required>
                    <option value="">Select...</option>
                    <option value="Amateur">Amateur</option>
                    <option value="Professional">Professional</option>
                  </select>
                ) : (
                  <span>{formData.careerLevel || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Years of Experience</label>
                {isEditing ? (
                  <input type="number" name="yearsExperience" value={formData.yearsExperience} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.yearsExperience || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Titles</label>
                {isEditing ? (
                  <input type="text" name="titles" value={formData.titles} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.titles || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Current Ranking</label>
                {isEditing ? (
                  <input type="text" name="currentRanking" value={formData.currentRanking} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.currentRanking || 'N/A'}</span>
                )}
              </div>
            </div>
          </div>
          {/* Performance Stats */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>Performance Stats</div>
            <div className={styles.cardContent}>
              <div className={styles.formGroup}><label className={styles.formLabel}>Games Played</label>
                {isEditing ? (
                  <input type="number" name="gamesPlayed" value={formData.gamesPlayed} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.gamesPlayed || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Average Score</label>
                {isEditing ? (
                  <input type="text" name="averageScore" value={formData.averageScore} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.averageScore || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Assists Per Game</label>
                {isEditing ? (
                  <input type="text" name="assistsPerGame" value={formData.assistsPerGame} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.assistsPerGame || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Win Rate (%)</label>
                {isEditing ? (
                  <input type="text" name="winRate" value={formData.winRate} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.winRate || 'N/A'}</span>
                )}
              </div>
            </div>
          </div>
          {/* Availability */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>Availability</div>
            <div className={styles.cardContent}>
              <div className={styles.formGroup}><label className={styles.formLabel}>Status</label>
                {isEditing ? (
                  <select name="status" value={formData.status} onChange={handleChange} className={styles.formInput} required>
                    <option value="">Select...</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Injured">Injured</option>
                  </select>
                ) : (
                  <span>{formData.status || 'N/A'}</span>
                )}
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>Preferred Play Time</label>
                {isEditing ? (
                  <input type="text" name="preferredPlayTime" value={formData.preferredPlayTime} onChange={handleChange} className={styles.formInput} required />
                ) : (
                  <span>{formData.preferredPlayTime || 'N/A'}</span>
                )}
              </div>
            </div>
          </div>
          {isEditing && (
            <div className={styles.buttonRow}>
              <button type="submit" className={styles.submitButton}>{isProfileExisting ? 'Save Changes' : 'Create Profile'}</button>
              {isProfileExisting && <button type="button" className={styles.cancelButton} onClick={handleCancel}>Cancel</button>}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default PlayerProfileForm;
